<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { TokenDiamondIcon } from '@uiKit/icons'

// Dark pill over the card photo. Shows token availability; when an investor
// count exists it cross-fades to it and back on a loop, with the pill width
// animating to fit each face. Static when there's no investors label or the
// user prefers reduced motion.

interface Props {
  tokensLabel: string
  investorsLabel?: string
}

const props = defineProps<Props>()

const FIRST_FLIP_MS = 2000
const FLIP_INTERVAL_MS = 2250
// Pill horizontal padding (10px each side) added to the active face width.
const PILL_PADDING_X_PX = 20

const showInvestors = ref(false)

// Raw <div ref>s — the faces' rendered widths are measured with a
// ResizeObserver (architecture §19 exception).
const tokensFaceRef = ref<HTMLElement | null>(null)
const investorsFaceRef = ref<HTMLElement | null>(null)
const tokensWidth = ref(0)
const investorsWidth = ref(0)

const pillStyle = computed(() => {
  if (!props.investorsLabel || tokensWidth.value <= 0 || investorsWidth.value <= 0) return undefined
  const faceWidth = showInvestors.value ? investorsWidth.value : tokensWidth.value
  return { width: `${faceWidth + PILL_PADDING_X_PX}px` }
})

let flipTimer: ReturnType<typeof setTimeout> | undefined
let resizeObserver: ResizeObserver | null = null

const scheduleFlip = (delay: number) => {
  flipTimer = setTimeout(() => {
    showInvestors.value = !showInvestors.value
    scheduleFlip(FLIP_INTERVAL_MS)
  }, delay)
}

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

onMounted(() => {
  if (!props.investorsLabel || prefersReducedMotion()) return

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      tokensWidth.value = tokensFaceRef.value?.offsetWidth ?? 0
      investorsWidth.value = investorsFaceRef.value?.offsetWidth ?? 0
    })
    if (tokensFaceRef.value) resizeObserver.observe(tokensFaceRef.value)
    if (investorsFaceRef.value) resizeObserver.observe(investorsFaceRef.value)
  }

  scheduleFlip(FIRST_FLIP_MS)
})

onBeforeUnmount(() => {
  if (flipTimer !== undefined) clearTimeout(flipTimer)
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<template>
  <div class="stat-chip" :style="pillStyle" :data-face="showInvestors ? 'investors' : 'tokens'">
    <div
      ref="tokensFaceRef"
      class="stat-chip__face"
      :class="{ 'stat-chip__face--hidden': showInvestors }"
      :aria-hidden="showInvestors"
    >
      <TokenDiamondIcon :size="11" />
      <Text is="span" type="caption-sm" color="rgb(255, 255, 255)" class="stat-chip__label">
        {{ tokensLabel }}
      </Text>
    </div>
    <div
      v-if="investorsLabel"
      ref="investorsFaceRef"
      class="stat-chip__face stat-chip__face--overlay"
      :class="{ 'stat-chip__face--hidden': !showInvestors }"
      :aria-hidden="!showInvestors"
    >
      <PiIcon name="users" class="stat-chip__icon" />
      <Text is="span" type="caption-sm" color="rgb(255, 255, 255)" class="stat-chip__label">
        {{ investorsLabel }}
      </Text>
    </div>
  </div>
</template>

<style scoped>
/* Overlay colours are fixed (not theme tokens) — the pill sits on a photo,
   so it stays dark-with-white-text in both light and dark mode. */
.stat-chip {
  position: relative;
  display: inline-flex;
  box-sizing: border-box;
  max-width: 66%;
  padding: 6px 10px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(16, 24, 40, 0.72);
  transition: width 250ms ease-in-out;
}

.stat-chip__face {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 14px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 250ms ease-in-out;
}

.stat-chip__face--overlay {
  position: absolute;
  top: 6px;
  left: 10px;
}

.stat-chip__face--hidden {
  opacity: 0;
}

.stat-chip__label {
  line-height: 14px;
}

.stat-chip__icon {
  font-size: 11px;
  color: rgb(255, 255, 255);
}

@media (prefers-reduced-motion: reduce) {
  .stat-chip,
  .stat-chip__face {
    transition: none;
  }
}
</style>
